import { Component, type ErrorInfo, type ReactNode } from "react";

/*
 * The DM Screen had NO error boundary, so a single throw during render/commit
 * unmounted the WHOLE tree and left a black screen until the DM reloaded the page
 * mid-session. This catches that throw instead:
 *   - the PANEL boundary (around the main content in Shell) shows a recoverable
 *     "this panel hit an error" card while the top bar, nav rail, activity rail
 *     and the live feeds keep running — the session is not lost;
 *   - the APP boundary (around the whole authenticated tree in App) is the
 *     last-resort catch so even a provider-level throw shows a reload card, never
 *     a blank page.
 * It also CAPTURES the error (console + an optional onError), which is how we
 * confirm the actual trigger of the black-screen in a live session.
 *
 * Happy path: render returns children verbatim with NO wrapper element, so it
 * does not change the DOM and cannot affect the markup-pinned layout tests.
 */

interface Props {
  children: ReactNode;
  /** What failed, for the fallback heading and the log line (e.g. "panel", "DM Screen"). */
  label?: string;
  /** A reset key: when it changes, the boundary clears a prior error and re-renders children. */
  resetKey?: unknown;
  /** Called once per caught error — e.g. to log it to the activity rail. */
  onError?: (error: Error, info: ErrorInfo) => void;
  /** Replace the default fallback. Receives the error and a reset() that clears it. */
  fallback?: (error: Error, reset: () => void) => ReactNode;
}
interface State { error: Error | null }

export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    // Always leave a breadcrumb — this is the error text that confirms the trigger.
    // eslint-disable-next-line no-console
    console.error(`[ErrorBoundary${this.props.label ? " · " + this.props.label : ""}]`, error, info?.componentStack);
    try { this.props.onError?.(error, info); } catch { /* a logger that throws must not re-crash the boundary */ }
  }

  componentDidUpdate(prev: Props): void {
    // A changed resetKey (e.g. the DM switched panels) clears a stale error so the
    // new content gets a fresh chance instead of staying stuck on the fallback.
    if (this.state.error && prev.resetKey !== this.props.resetKey) this.setState({ error: null });
  }

  reset = (): void => this.setState({ error: null });

  render(): ReactNode {
    const { error } = this.state;
    if (!error) return this.props.children;
    if (this.props.fallback) return this.props.fallback(error, this.reset);

    const label = this.props.label || "This view";
    return (
      <div className="error-boundary" role="alert">
        <h3>⚠ {label} hit an error</h3>
        <p className="muted">The rest of the DM Screen is still running. Try again, or reload if it keeps happening.</p>
        <pre className="error-boundary-detail">{error.message || String(error)}</pre>
        <div className="error-boundary-actions">
          <button type="button" onClick={this.reset}>Try again</button>
          <button type="button" onClick={() => window.location.reload()}>Reload page</button>
        </div>
        {error.stack ? (
          <details>
            <summary className="muted">Error details (for a bug report)</summary>
            <pre className="error-boundary-stack">{error.stack}</pre>
          </details>
        ) : null}
      </div>
    );
  }
}
